import { type FC, useContext, useEffect, useRef } from 'react';
import AddIcon from '@assets/img/icons/add-icon.svg?react';
import AddActiveIcon from '@assets/img/icons/add-icon-active.svg?react';
import { DiceGameContext } from '@components/DiceGame/context.ts';
import cn from 'classnames';
import { AnimatePresence } from 'framer-motion';

import { ModalContext } from '@/components/Modals/Modal/config';
import { H1 } from '@/components/Typography/Typography';
import { MotionPicture } from '@/components/ui/Picture/Picture';

import css from './styles.module.scss';

import dicePng from '/img/dice-game/dice.png';
import diceWebp from '/img/dice-game/dice.webp';
import dicePurplePng from '/img/dice-game/dice-purple.png';
import dicePurpleWebp from '/img/dice-game/dice-purple.webp';
import diceYellowPng from '/img/dice-game/dice-yellow.png';
import diceYellowWebp from '/img/dice-game/dice-yellow.webp';

interface DiceCountProps {
  quantity: number;
  className?: string;
  withAddButton?: boolean;
  variant?: 'default' | 'stars-mini-game' | 'tgem-mini-game';
  scale?: number;
}

const DiceCount: FC<DiceCountProps> = ({
  quantity,
  className,
  withAddButton,
  variant = 'default',
  scale,
}) => {
  const { setDiceToRef } = useContext(DiceGameContext);
  const targetRef = useRef<HTMLImageElement | null>(null);
  const { openModal } = useContext(ModalContext);

  useEffect(() => {
    if (targetRef.current) {
      setDiceToRef(targetRef);
    }
  }, []);

  const images = {
    'stars-mini-game': {
      png: diceYellowPng,
      webp: diceYellowWebp,
    },
    'tgem-mini-game': {
      png: dicePurplePng,
      webp: dicePurpleWebp,
    },
    default: {
      png: dicePng,
      webp: diceWebp,
    },
  };

  const diceImage = images[variant];
  const isMinigame = variant !== 'default';

  return (
    <div
      onClick={
        withAddButton
          ? (): void =>
              openModal({ renderKind: 'buyDiceModal', props: undefined })
          : undefined
      }
      className={cn(
        css.diceCount,
        className,
        isMinigame && css['diceCount--minigame'],
      )}
    >
      <AnimatePresence>
        <MotionPicture
          ref={targetRef}
          className={cn(
            'w-8 h-8 shrink-0',
            isMinigame && '[&_img]:drop-shadow-[0_4px_8px_rgba(0,0,0,0.6)]',
          )}
          transition={{
            layout: { duration: 0.6, ease: 'easeOut' },
            scale: { duration: 0.3, delay: 0.3 },
          }}
          img={diceImage.png}
          webp={diceImage.webp}
          alt="dice"
          animate={{ scale }}
        />
      </AnimatePresence>
      <H1 weight="bold" className={!withAddButton ? 'mr-2' : ''}>
        {quantity}
      </H1>
      {withAddButton && (
        <>
          {quantity > 0 ? (
            <AddIcon className="w-8 h-8 shrink-0 !text-white/40" />
          ) : (
            <AddActiveIcon className="w-8 h-8 shrink-0" />
          )}
        </>
      )}
    </div>
  );
};

export { DiceCount };
